/**
 * Created by mongoose on 15/09/16.
 */
import constants from "../../utils/constants";
import moment from "moment";
import { getCalendarKey } from "./util";
/*
{
  "date": "2016-10-01T09:21:52.800Z",
  "rate": 237,
  "availability": 1,
  "closed": false,
  "minStay": 2,
  "maxStay": null,
  "minNotice": 336,
  "maxNotice": null
}
*/

function ARIObject(startDate, endDate, rawARI) {
  const start = moment(startDate).startOf("day").toDate();
  const end = moment(endDate).startOf("day").toDate();
  let dateIdx = moment(start).toDate();
  this.rawARI = {};
  while (dateIdx.getTime() < end.getTime()) {
    const key = getCalendarKey(dateIdx);
    this.rawARI[key] = rawARI[key];
    dateIdx = moment(dateIdx).add(1, "day").toDate();
  }
  this.getValues = property => {
    const rawARITmp = this.rawARI;
    const values = Object.keys(rawARITmp)
      .map(key => rawARITmp[key] && rawARITmp[key][property])
      .filter(it => it != null); // eslint-disable-line
    return values && values.length > 0 ? values : null;
  };
  this.maxValue = property => {
    const values = this.getValues(property);
    return values ? Math.max(...values) : null;
  };
  this.minValue = property => {
    const values = this.getValues(property);
    return values ? Math.min(...values) : null;
  };
  this.firstValue = property => {
    return (
      (this.rawARI[getCalendarKey(start)] && this.rawARI[getCalendarKey(start)][property]) || null
    );
  };
  this.contains = (property, value) => {
    const values = this.getValues(property);
    return values ? values.indexOf(value) !== -1 : false;
  };
  this.isEmpty = property => {
    const values = this.getValues(property);
    return values === null || values.length <= 0;
  };
}

function FilterError(valueType, status, value) {
  this.status = status;
  this.valueType = valueType;
  this.value = value;
}

function FilterResult(startDate, endDate, ARI, isPropertyCalendar) {
  this.startDate = moment(startDate).startOf("day").toDate();
  this.endDate = moment(endDate).startOf("day").toDate();
  this.ari = new ARIObject(startDate, endDate, ARI);
  this.errors = [];
  this.status = null;
  this.finalError = null;
  this.isPropertyCalendar = isPropertyCalendar;
  this.filter = filterFunction => {
    if (
      (this.status !== null && this.status === constants.BOOKABLE_STATUS.UNAVAILABLE) ||
      this.status === constants.BOOKABLE_STATUS.RESTRICTED
    ) {
      return this;
    }
    const error = filterFunction({ startDate, endDate, ARI: this.ari, isPropertyCalendar });

    if (!error) {
      return this;
    }

    this.errors.push(error);

    this.status = error.status;

    this.finalError = error;

    return this;
  };
}

const filterResultFactory = (startDate, endDate, ARI, isPropertyCalendar) =>
  new FilterResult(startDate, endDate, ARI, isPropertyCalendar);

function filterByCheckInCheckOut() {
  /* TODO: IMPLEMENT */
  return null;
}

function filterByMinStay({ startDate, endDate, ARI }) {
  const maxMinStayValueForPeriod = ARI.maxValue(constants.ARI_VALUE_TYPE.MIN_STAY);
  if (maxMinStayValueForPeriod !== null) {
    const numberOfDaysForStay = moment.duration(moment(endDate).diff(startDate)).asDays();
    const isValid = numberOfDaysForStay >= maxMinStayValueForPeriod;
    return !isValid
      ? new FilterError(
          constants.ARI_VALUE_TYPE.MIN_STAY,
          constants.BOOKABLE_STATUS.RESTRICTED,
          maxMinStayValueForPeriod
        )
      : null;
  }
  return null;
}

function filterByMaxStay({ startDate, endDate, ARI }) {
  const minMaxStayValueForPeriod = ARI.minValue(constants.ARI_VALUE_TYPE.MAX_STAY);
  if (minMaxStayValueForPeriod !== null) {
    const numberOfDaysForStay = moment.duration(moment(endDate).diff(startDate)).asDays();
    const isValid = numberOfDaysForStay <= minMaxStayValueForPeriod;
    return !isValid
      ? new FilterError(
          constants.ARI_VALUE_TYPE.MAX_STAY,
          constants.BOOKABLE_STATUS.RESTRICTED,
          minMaxStayValueForPeriod
        )
      : null;
  }
  return null;
}

function filterByMinNotice({ startDate, ARI }) {
  const firstNoticeValueForPeriod = ARI.firstValue(constants.ARI_VALUE_TYPE.MIN_NOTICE);
  if (firstNoticeValueForPeriod !== null) {
    const numberOfHoursBeforeStay = moment
      .duration(moment(startDate).endOf("day").diff(moment()))
      .asHours();
    let isValid = true;
    if (numberOfHoursBeforeStay <= 0) {
      isValid = false;
    } else {
      isValid = numberOfHoursBeforeStay >= firstNoticeValueForPeriod;
    }
    return !isValid
      ? new FilterError(
          constants.ARI_VALUE_TYPE.MIN_NOTICE,
          constants.BOOKABLE_STATUS.RESTRICTED,
          firstNoticeValueForPeriod
        )
      : null;
  }
  return null;
}

function filterByMaxNotice({ startDate, ARI }) {
  const firstNoticeValueForPeriod = ARI.firstValue(constants.ARI_VALUE_TYPE.MAX_NOTICE);
  if (firstNoticeValueForPeriod !== null) {
    const numberOfHoursBeforeStay = moment
      .duration(moment(startDate).endOf("day").diff(moment()))
      .asHours();
    let isValid = true;
    if (numberOfHoursBeforeStay <= 0) {
      isValid = false;
    } else {
      isValid = numberOfHoursBeforeStay <= firstNoticeValueForPeriod;
    }
    return !isValid
      ? new FilterError(
          constants.ARI_VALUE_TYPE.MAX_NOTICE,
          constants.BOOKABLE_STATUS.RESTRICTED,
          firstNoticeValueForPeriod
        )
      : null;
  }
  return null;
}

function filterByClosed({ ARI }) {
  const isLocked = ARI.contains(constants.ARI_VALUE_TYPE.LOCK, true);
  return isLocked
    ? new FilterError(constants.ARI_VALUE_TYPE.LOCK, constants.BOOKABLE_STATUS.UNAVAILABLE, true)
    : null;
}
function filterByAvailability({ ARI }) {
  const isValueRangeNotContinuous =
    Object.keys(ARI.rawARI).length !==
    (ARI.getValues(constants.ARI_VALUE_TYPE.AVAILABILITY) &&
      ARI.getValues(constants.ARI_VALUE_TYPE.AVAILABILITY).length);
  const isUnavailable =
    isValueRangeNotContinuous ||
    ARI.contains(constants.ARI_VALUE_TYPE.AVAILABILITY, 0) ||
    ARI.contains(constants.ARI_VALUE_TYPE.AVAILABILITY, 0.0) ||
    ARI.contains(constants.ARI_VALUE_TYPE.AVAILABILITY, null);
  return isUnavailable
    ? new FilterError(
        constants.ARI_VALUE_TYPE.AVAILABILITY,
        constants.BOOKABLE_STATUS.UNAVAILABLE,
        true
      )
    : null;
}

// When rooms are locked in an inventory Calendar, they still return availability but NOT rates
// Therefore we must filter them by rates as well to disable them
function filterByRate({ ARI, isPropertyCalendar }) {
  if (isPropertyCalendar) {
    return null;
  }
  const areRatesInvalid = ARI.isEmpty(constants.ARI_VALUE_TYPE.RATE);
  return areRatesInvalid
    ? new FilterError(constants.ARI_VALUE_TYPE.RATE, constants.BOOKABLE_STATUS.UNAVAILABLE, true)
    : null;
}

/* TODO: can the filterByRate be disabled? */
export function filter(startDate, endDate, ARI, isPropertyCalendar) {
  return filterResultFactory(startDate, endDate, ARI, isPropertyCalendar)
    .filter(filterByClosed)
    .filter(filterByAvailability)
    .filter(filterByRate)
    .filter(filterByCheckInCheckOut)
    .filter(filterByMinStay)
    .filter(filterByMaxStay)
    .filter(filterByMinNotice)
    .filter(filterByMaxNotice);
}
